(() => {
  const FLAG = 'dc-native-file-cart-open';
  const DB_NAME = 'dc-upload-session-v1';
  const STORE_NAME = 'files';
  const SESSION_KEY = 'dc-upload-session-id';
  const CHOICE_PREFIX = 'dc-upload-choice:';
  const TTL_MS = 6 * 60 * 60 * 1000;
  const FILE_SELECTOR = 'input[type="file"][name^="properties["]';
  let dbPromise = null;

  function normalizePath() {
    return window.location.pathname
      .replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '')
      .replace(/\/$/, '') || '/';
  }

  function sessionId() {
    try {
      let value = sessionStorage.getItem(SESSION_KEY);
      if (!value) {
        value = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(SESSION_KEY, value);
      }
      return value;
    } catch {
      return 'dc-session-fallback';
    }
  }

  function fileKey(input) {
    return `${sessionId()}|${normalizePath()}|${input.name}`;
  }

  function choiceKey(select) {
    return `${CHOICE_PREFIX}${normalizePath()}|${select.name}`;
  }

  function openDb() {
    if (!('indexedDB' in window)) return Promise.resolve(null);
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, 1);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      };

      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const db = request.result;
        resolve(db);

        try {
          const transaction = db.transaction(STORE_NAME, 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          const currentSession = sessionId();
          const now = Date.now();
          const cursor = store.openCursor();
          cursor.onsuccess = () => {
            const item = cursor.result;
            if (!item) return;
            const record = item.value;
            if (record?.sessionId !== currentSession || now - Number(record?.updatedAt || 0) > TTL_MS) {
              item.delete();
            }
            item.continue();
          };
        } catch {}
      };
    });

    return dbPromise;
  }

  async function saveFile(input, file) {
    const db = await openDb();
    if (!db || !(file instanceof File)) return;

    try {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put({
        id: fileKey(input),
        sessionId: sessionId(),
        path: normalizePath(),
        inputName: input.name,
        file,
        fileName: file.name,
        fileType: file.type,
        lastModified: file.lastModified,
        updatedAt: Date.now()
      });
    } catch {}
  }

  async function deleteFile(input) {
    const db = await openDb();
    if (!db) return;

    try {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).delete(fileKey(input));
    } catch {}
  }

  async function readFile(input) {
    const db = await openDb();
    if (!db) return null;

    return new Promise((resolve) => {
      try {
        const transaction = db.transaction(STORE_NAME, 'readonly');
        const request = transaction.objectStore(STORE_NAME).get(fileKey(input));
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  }

  function rememberChoice(select) {
    try {
      sessionStorage.setItem(choiceKey(select), select.value);
    } catch {}
  }

  function restoreChoices(root = document) {
    const selects = [];
    if (root instanceof HTMLSelectElement && root.name === 'properties[Fotobewerking]') selects.push(root);
    root.querySelectorAll?.('select[name="properties[Fotobewerking]"]').forEach((select) => selects.push(select));

    selects.forEach((select) => {
      try {
        const saved = sessionStorage.getItem(choiceKey(select));
        if (saved === null || select.value === saved) return;
        select.value = saved;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } catch {}
    });
  }

  async function restoreInput(input, forceExisting = false) {
    if (!(input instanceof HTMLInputElement) || !input.matches(FILE_SELECTOR)) return;

    const existing = input.files?.[0];
    if (existing) {
      if (forceExisting) input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const record = await readFile(input);
    if (!record?.file) return;

    try {
      const restored = record.file instanceof File
        ? record.file
        : new File([record.file], record.fileName || 'foto.jpg', {
            type: record.fileType || 'image/jpeg',
            lastModified: record.lastModified || Date.now()
          });
      const transfer = new DataTransfer();
      transfer.items.add(restored);
      input.files = transfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch {}
  }

  function restoreAll(root = document, forceExisting = false) {
    restoreChoices(root);

    const inputs = [];
    if (root instanceof HTMLInputElement && root.matches(FILE_SELECTOR)) inputs.push(root);
    root.querySelectorAll?.(FILE_SELECTOR).forEach((input) => inputs.push(input));
    inputs.forEach((input) => restoreInput(input, forceExisting));
  }

  function scheduleRestore(forceExisting = false) {
    [0, 80, 260, 700].forEach((delay) => {
      window.setTimeout(() => restoreAll(document, forceExisting), delay);
    });
  }

  function installPersistence() {
    document.addEventListener('change', (event) => {
      const target = event.target;

      if (target instanceof HTMLSelectElement && target.name === 'properties[Fotobewerking]') {
        rememberChoice(target);
        return;
      }

      if (!(target instanceof HTMLInputElement) || !target.matches(FILE_SELECTOR)) return;
      const file = target.files?.[0];
      if (file) saveFile(target, file);
      else deleteFile(target);
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) restoreAll(node, false);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.addEventListener('pageshow', () => scheduleRestore(true));
    scheduleRestore(false);
  }

  function selectedFileInputs(form) {
    return Array.from(form.querySelectorAll('input[type="file"][name^="properties["]:not([disabled])')).filter(
      (input) => input instanceof HTMLInputElement && input.files && input.files.length > 0
    );
  }

  async function nativeSubmitWithFiles(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(form.action || window.location.href, window.location.href);
    if (!action.pathname.includes('/cart/add')) return;
    if (!selectedFileInputs(form).length) return;

    // File-personalisation must use exactly one add-to-cart request.
    // A native multipart submit can conflict with Shopify bundle expansion,
    // causing the parent bundle to appear twice for products that use shared stock.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.dcNativeFileSubmitting === 'true') return;
    form.dataset.dcNativeFileSubmitting = 'true';

    const submitters = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
    submitters.forEach((button) => {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) button.disabled = true;
    });

    try {
      const root = window.Shopify?.routes?.root || '/';
      const formData = new FormData(form);

      // Shopify officially supports FormData on the Ajax Cart API, including
      // line-item properties and native file uploads.
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        let message = 'Toevoegen aan de winkelwagen is niet gelukt.';
        try {
          const error = await response.json();
          message = error?.description || error?.message || message;
        } catch {}
        throw new Error(message);
      }

      sessionStorage.setItem(FLAG, '1');
      window.location.reload();
    } catch (error) {
      console.error('[Domstad] Foto-personalisatie kon niet aan de winkelwagen worden toegevoegd.', error);
      delete form.dataset.dcNativeFileSubmitting;
      submitters.forEach((button) => {
        if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) button.disabled = false;
      });

      const errorBox = form.querySelector('.product-form-text__error');
      if (errorBox instanceof HTMLElement) {
        errorBox.classList.remove('hidden');
        errorBox.textContent = error instanceof Error ? error.message : 'Toevoegen aan de winkelwagen is niet gelukt.';
      }
    }
  }

  function reopenCartDrawer() {
    if (sessionStorage.getItem(FLAG) !== '1') return;
    sessionStorage.removeItem(FLAG);

    const open = () => {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        requestAnimationFrame(() => drawer.open());
      }
    };

    if (customElements.get('theme-drawer')) open();
    else customElements.whenDefined('theme-drawer').then(open);
  }

  // Cart submission is intentionally left to Shopify's existing product-form component.
  // That component already sends the complete FormData, including native file properties,
  // to /cart/add.js. A second submit listener here caused personalised photo products
  // to be added twice and forced an unnecessary page reload.
  installPersistence();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reopenCartDrawer, { once: true });
  } else {
    reopenCartDrawer();
  }
})();